import { useCallback, useRef, useSyncExternalStore } from 'react';

import type { ReadableStore, ViewportState } from '@frontend/viewer-core';

import type {
  DrawingModel,
  DxfSceneLoadState,
  DxfSessionState,
  DxfViewerSession,
} from './types';

/**
 * Subscribes to a `ReadableStore`, with the reference stability
 * `useSyncExternalStore` requires.
 *
 * `getSnapshot` must return the same reference between real changes or React
 * re-renders forever, so the selector's result is cached against the state object it
 * came from. `isEqual` covers selectors that build a new value each call — an array
 * of ids, a derived object — where identity alone would always look changed.
 *
 * Prefer a controller lens (`session.tools`, `session.overlays`, …) over
 * `session.state` when one exists — the lens already filters notifications.
 *
 * Written by hand rather than pulled from `use-sync-external-store/with-selector`:
 * React 18 ships the hook itself but not the selector variant, and that package is
 * only present transitively here.
 */
export const useReadableStore = <S, T>(
  store: ReadableStore<S>,
  select: (state: S) => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
): T => {
  const selectRef = useRef(select);
  selectRef.current = select;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;

  const cacheRef = useRef<{ state: S; value: T } | null>(null);

  const getSnapshot = useCallback(() => {
    const state = store.getState();
    const cached = cacheRef.current;
    if (cached && Object.is(cached.state, state)) {
      return cached.value;
    }

    const value = selectRef.current(state);
    // Hold onto the previous reference when the values are equivalent, so a change
    // in an unrelated slice does not re-render this consumer.
    const stable =
      cached && isEqualRef.current(cached.value, value) ? cached.value : value;
    cacheRef.current = { state, value: stable };
    return stable;
  }, [store]);

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
};

/**
 * Reads a slice of the session's **reactive tier**.
 *
 * Prefer `useReadableStore` on a controller lens when one exists. Reach for this
 * only for fields that have no public store yet, or for ad-hoc cross-field selects.
 */
export const useSessionState = <T>(
  session: DxfViewerSession,
  select: (state: DxfSessionState) => T,
  isEqual?: (a: T, b: T) => boolean,
): T => useReadableStore(session.state, select, isEqual);

/**
 * Reads a slice of the **live tier**.
 *
 * This re-renders at frame rate for the whole of a pan or zoom, which is precisely
 * what the session exists to avoid. Reach for it only when a value genuinely has to
 * pass through React; the cheaper answer is almost always to subscribe to
 * `session.viewport` and write to the DOM directly, as the text layer does.
 */
export const useViewportValue = <T>(
  session: DxfViewerSession,
  select: (viewport: ViewportState) => T,
  isEqual?: (a: T, b: T) => boolean,
): T => useReadableStore(session.viewport, select, isEqual);

/**
 * The drawing load lifecycle. Branch on `status`, then read `model` / `scene`
 * without further null checks when `'ready'`.
 */
export const useDxfLoad = (session: DxfViewerSession): DxfSceneLoadState =>
  useReadableStore(session.load, (load) => load);

/**
 * Reads the derived drawing model.
 *
 * Prefer `useDxfLoad` when readiness is a mount gate. This helper is for
 * selectors that already tolerate `null` (e.g. optional units before the
 * first scene).
 */
export const useSessionModel = <T>(
  session: DxfViewerSession,
  select: (model: DrawingModel | null) => T,
  isEqual?: (a: T, b: T) => boolean,
): T => useReadableStore(session.model, select, isEqual);
